import {
  NotificationType,
  NotificationPriority,
  PriorityConfig,
  NotificationTypeConfig,
  DateFormatOptions,
} from '@/pages/NotificationCenter/types'
import { EventPayloadId } from '@/pages/NotificationCenter/notificationMetadataTypes'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import TimelineIcon from '@mui/icons-material/Timeline'
import InfoIcon from '@mui/icons-material/Info'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import NotificationsIcon from '@mui/icons-material/Notifications'
import BuildIcon from '@mui/icons-material/Build'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import DescriptionIcon from '@mui/icons-material/Description'
import HandshakeIcon from '@/components/Icons/NotificationCenterIcons/HandshakeIcon'
import MoneyIcon from '@/components/Icons/NotificationCenterIcons/MoneyIcon'
import DocumentDismissIcon from '@/components/Icons/NotificationCenterIcons/DocumentDismissIcon'
import DocumentClockIcon from '@/components/Icons/NotificationCenterIcons/DocumentClockIcon'
import DocumentPercentIcon from '@/components/Icons/NotificationCenterIcons/DocumentPercentIcon'
import ClockDismissIcon from '@/components/Icons/NotificationCenterIcons/ClockDismissIcon'
import BriefcaseSearchIcon from '@/components/Icons/NotificationCenterIcons/BriefcaseSearchIcon'
import DocumentAddIcon from '@/components/Icons/NotificationCenterIcons/DocumentAddIcon'
import PersonDeleteIcon from '@/components/Icons/NotificationCenterIcons/PersonDeleteIcon'
import DocumentLockIcon from '@/components/Icons/NotificationCenterIcons/DocumentLockIcon'
import DocumentCheckmarkIcon from '@/components/Icons/NotificationCenterIcons/DocumentCheckmarkIcon'
import PersonAddIcon from '@/components/Icons/NotificationCenterIcons/PersonAddIcon'
import PersonArrowRightIcon from '@/components/Icons/NotificationCenterIcons/PersonArrowRightIcon'
import BuildingPeopleIcon from '@/components/Icons/NotificationCenterIcons/BuildingPeopleIcon'

// ================================
// CONSTANTS
// ================================

const notificationColorPalette = {
  // Blues
  blue500: '#3B82F6',
  blueBg: '#DBEAFE',
  cyan700: '#007A95',
  cyanBg: '#D0FCFF',

  // Greens
  darkCyanGreen: '#009366',
  green500: '#10B981',
  greenBg: '#CFFED7',

  // Yellows/Ambers
  amber500: '#F59E0B',
  orange700: '#672100',
  orangeBg: '#FFEAE0',

  // Reds
  red500: '#EF4444',
  darkRed: '#620000',
  redBg: '#FFE3E7',

  // Purples
  purple500: '#8B5CF6',
  purpleBg: '#CBC3E3',

  // Grays
  gray500: '#6B7280',
}

/**
 * Priority level configurations with colors and labels
 */
export const PRIORITY_CONFIGS: Record<NotificationPriority, PriorityConfig> = {
  0: {
    label: 'Low',
    color: notificationColorPalette.gray500,
  },
  1: {
    label: 'Normal',
    color: notificationColorPalette.blue500,
  },
  2: {
    label: 'Medium',
    color: notificationColorPalette.amber500,
  },
  3: {
    label: 'High',
    color: notificationColorPalette.red500,
  },
}

/**
 * Shared configuration objects for notification types with identical display
 * Reduces duplication and makes maintenance easier
 */
const SHARED_CONFIGS = {
  proposalReceived: {
    label: 'New Proposal',
    description: 'Buyer submitted a proposal for case',
    icon: FolderOpenIcon,
    iconColor: notificationColorPalette.amber500,
  },
  invitationReceived: {
    label: 'New Invitation',
    description: 'Seller Attorney invited you to a case',
    icon: HandshakeIcon,
    iconColor: notificationColorPalette.purple500,
    bgColor: notificationColorPalette.purpleBg,
  },
  proposalDeclined: {
    label: 'Proposal Declined',
    description: 'Case proposal was not selected',
    icon: CancelIcon,
    iconColor: notificationColorPalette.red500,
    bgColor: notificationColorPalette.redBg,
  },
  counterofferReceived: {
    label: 'New Counteroffer',
    description: 'Attorney sent you a counteroffer',
    icon: SwapHorizIcon,
    iconColor: notificationColorPalette.amber500,
  },
  proposalExpired: {
    label: 'Proposal Expired',
    description: 'Proposal for case expired',
    icon: DocumentClockIcon,
    iconColor: notificationColorPalette.darkRed,
    bgColor: notificationColorPalette.redBg,
  },
  caseMatched: {
    label: 'New Match!',
    description: 'Case has been matched',
    icon: HandshakeIcon,
    iconColor: notificationColorPalette.darkCyanGreen,
    bgColor: notificationColorPalette.greenBg,
  },
  clientSigned: {
    label: 'Client Signed',
    description: 'Client signed for this case',
    icon: HandshakeIcon,
    iconColor: notificationColorPalette.darkCyanGreen,
    bgColor: notificationColorPalette.greenBg,
  },
  proposalWithdrawn: {
    label: 'Proposal Withdrawn',
    description: 'Proposal was withdrawn',
    icon: RemoveCircleIcon,
    iconColor: notificationColorPalette.gray500,
  },
  caseRemoved: {
    label: 'Case Removed',
    description: 'Case has been removed',
    icon: DocumentDismissIcon,
    iconColor: notificationColorPalette.darkRed,
    bgColor: notificationColorPalette.redBg,
  },
  clientNotSigned: {
    label: 'Client Not Signed',
    description: 'Client could not be signed',
    icon: PersonDeleteIcon,
    iconColor: notificationColorPalette.darkRed,
    bgColor: notificationColorPalette.redBg,
  },
  contactInfoShared: {
    label: 'Contact Info Shared',
    description: 'Case contact information shared',
    icon: InfoIcon,
    iconColor: notificationColorPalette.blue500,
    // Without a background the glyph sits bare and reads smaller than the
    // other rows' icons, which all have a tinted circle (ENG-2066).
    bgColor: notificationColorPalette.blueBg,
  },
}

/**
 * Notification type configurations with labels and colors
 * Maps all 61 backend EventPayloadId types to display configurations
 * Organized by category matching Figma designs
 */
export const NOTIFICATION_TYPE_CONFIGS: Partial<
  Record<NotificationType, NotificationTypeConfig>
> = {
  // ================================
  // CASE MANAGERS
  // ================================
  [EventPayloadId.CASE_CASE_MANAGER_ASSIGNED]: {
    label: 'New Case',
    description: 'A new case has been assigned to you',
    icon: PersonAddIcon,
    iconColor: notificationColorPalette.cyan700,
    bgColor: notificationColorPalette.cyanBg,
  },
  [EventPayloadId.CASE_CREATED_ON_YOUR_BEHALF]: {
    label: 'Case Created',
    description: 'A proxy user created a case on your behalf',
    icon: DocumentAddIcon,
    iconColor: notificationColorPalette.cyan700,
    bgColor: notificationColorPalette.cyanBg,
  },
  [EventPayloadId.CASE_PROPOSAL_CREATED_ON_YOUR_BEHALF]: {
    label: 'Proposal Sent',
    description: 'A proxy sent a case on your behalf',
    icon: PersonArrowRightIcon,
    iconColor: notificationColorPalette.cyan700,
    bgColor: notificationColorPalette.cyanBg,
  },

  // ================================
  // NEGOTIATIONS
  // ================================
  // Proposals - use shared config
  [EventPayloadId.PUBLIC_CASE_PROPOSAL_OWNER]: SHARED_CONFIGS.proposalReceived,
  [EventPayloadId.PRIVATE_CASE_PROPOSAL_OWNER]: SHARED_CONFIGS.proposalReceived,
  [EventPayloadId.PRIVATE_CASE_PROPOSAL_PROSPECT]:
    SHARED_CONFIGS.proposalReceived,

  // Invitations - use shared config
  [EventPayloadId.PUBLIC_CASE_INVITE_PROSPECT]:
    SHARED_CONFIGS.invitationReceived,
  [EventPayloadId.PUBLIC_CASE_INVITE_OWNER]: SHARED_CONFIGS.invitationReceived,
  [EventPayloadId.PRIVATE_CASE_INVITE_PROSPECT]:
    SHARED_CONFIGS.invitationReceived,

  // Declined Proposals - use shared config
  [EventPayloadId.PUBLIC_CASE_PROPOSAL_REJECTED_BY_OWNER]:
    SHARED_CONFIGS.proposalDeclined,
  [EventPayloadId.PUBLIC_CASE_PROPOSAL_REJECTED_BY_PROSPECT]:
    SHARED_CONFIGS.proposalDeclined,
  [EventPayloadId.PRIVATE_CASE_PROPOSAL_REJECTED_BY_OWNER]:
    SHARED_CONFIGS.proposalDeclined,
  [EventPayloadId.PRIVATE_CASE_PROPOSAL_REJECTED_BY_PROSPECT]:
    SHARED_CONFIGS.proposalDeclined,

  // Counteroffers - use shared config for receiver, custom for maker
  [EventPayloadId.COUNTER_OFFER_RECEIVER]: SHARED_CONFIGS.counterofferReceived,
  [EventPayloadId.CASE_COUNTEROFFER_OWNER]: SHARED_CONFIGS.counterofferReceived,
  [EventPayloadId.CASE_COUNTEROFFER_PROSPECT]:
    SHARED_CONFIGS.counterofferReceived,
  [EventPayloadId.COUNTER_OFFER_MAKER]: {
    label: 'Counteroffer Sent',
    description: 'Your counteroffer was sent',
    icon: SwapHorizIcon,
    iconColor: notificationColorPalette.blue500,
  },

  // Proposal Expiration - use shared config
  [EventPayloadId.CASE_EXPIRED_PROPOSAL_CASE_OWNER]:
    SHARED_CONFIGS.proposalExpired,
  [EventPayloadId.CASE_EXPIRED_PROPOSAL_PROSPECT]:
    SHARED_CONFIGS.proposalExpired,
  [EventPayloadId.CASE_PROPOSAL_REMINDER_CASE_OWNER]: {
    label: 'Proposal close to expire',
    description: 'Proposal is about to expire',
    icon: DocumentClockIcon,
    iconColor: notificationColorPalette.orange700,
    bgColor: notificationColorPalette.orangeBg,
  },

  // Case Matched - use shared config
  [EventPayloadId.CASE_MATCHED_OWNER]: SHARED_CONFIGS.caseMatched,
  [EventPayloadId.CASE_MATCHED_PROSPECT]: SHARED_CONFIGS.caseMatched,
  [EventPayloadId.PUBLIC_CASE_MATCHED_CHARGED_BUYER]: {
    label: 'Match Fee Charged',
    description: 'We charged you for signing client',
    icon: MoneyIcon,
    iconColor: notificationColorPalette.cyan700,
    bgColor: notificationColorPalette.cyanBg,
  },
  [EventPayloadId.CASE_MATCHED_BY_ANOTHER_ATTORNEY_PROSPECT]: {
    label: 'Buyer with negotiations for a Case Signed by another attorney',
    description:
      'Buyer with negotiations for a Case Signed by another attorney',
    icon: DocumentDismissIcon,
    iconColor: notificationColorPalette.darkRed,
    bgColor: notificationColorPalette.redBg,
  },
  [EventPayloadId.CASE_MATCH_REMINDER_PROSPECT]: {
    label: 'Client Signed Reminder',
    description:
      'You have 24 hours left to let us know if you signed the client.',
    icon: DocumentClockIcon,
    iconColor: notificationColorPalette.orange700,
    bgColor: notificationColorPalette.orangeBg,
  },

  // ================================
  // WATERFALLS (4 types)
  // ================================
  [EventPayloadId.CASE_WATERFALL_INVITATION_EXPIRED_PROSPECT]: {
    label: 'Waterfall Invitation Expired',
    description: 'Case invitation expired',
    icon: ClockDismissIcon,
    iconColor: notificationColorPalette.darkRed,
    bgColor: notificationColorPalette.redBg,
  },
  [EventPayloadId.CASE_PRIVATE_EXPIRING_WATERFALL_PROSPECT]: {
    label: 'Priority Access Expiring Soon',
    description: 'Case invitation will expire in 24 hours',
    icon: TimelineIcon,
    iconColor: notificationColorPalette.orange700,
    bgColor: notificationColorPalette.orangeBg,
  },
  [EventPayloadId.PRIVATE_CASE_ALL_WATERFALLS_EXPIRED_OWNER]: {
    label: 'Case Posted to Marketplace',
    description:
      'No waterfall proposals received. Your case posted to marketplace',
    icon: BriefcaseSearchIcon,
    iconColor: notificationColorPalette.cyan700,
    bgColor: notificationColorPalette.cyanBg,
  },
  [EventPayloadId.CASE_AVAILABLE_PROSPECT]: {
    label: 'Case Available',
    description: 'New case available in marketplace',
    icon: FolderOpenIcon,
    iconColor: notificationColorPalette.green500,
  },

  // ================================
  // BROADCAST (MPN V2)
  // ================================
  [EventPayloadId.PRIVATE_CASE_BROADCAST_INVITE_PROSPECT]: {
    label: 'New Broadcast Invitation',
    description:
      'Seller invited you to a case - first attorney to accept gets it',
    icon: HandshakeIcon,
    iconColor: notificationColorPalette.purple500,
    bgColor: notificationColorPalette.purpleBg,
  },

  // ================================
  // DATA ROOM (4 types)
  // ================================
  [EventPayloadId.PUBLIC_CASE_DATA_ROOM_REQUESTED_CASE_OWNER]: {
    label: 'Data Room Request',
    description: 'Buyer requested access to data room',
    icon: DocumentLockIcon,
    iconColor: notificationColorPalette.cyan700,
    bgColor: notificationColorPalette.cyanBg,
  },
  [EventPayloadId.PUBLIC_CASE_DATA_ROOM_GRANTED_ACCESS_PROSPECT]: {
    label: 'Data Room Granted',
    description: 'You now have data room access',
    icon: DocumentCheckmarkIcon,
    iconColor: notificationColorPalette.darkCyanGreen,
    bgColor: notificationColorPalette.greenBg,
  },
  [EventPayloadId.PUBLIC_CASE_DATA_ROOM_DENIED_PROSPECT]: {
    label: 'Data Room Denied',
    description: 'Your request for data room access was denied',
    icon: DocumentDismissIcon,
    iconColor: notificationColorPalette.darkRed,
    bgColor: notificationColorPalette.redBg,
  },
  [EventPayloadId.PUBLIC_CASE_DATA_ROOM_REMOVED_PROSPECT]: {
    label: 'Data Room Removed',
    description: 'You no longer have access to data room',
    icon: DocumentDismissIcon,
    iconColor: notificationColorPalette.darkRed,
    bgColor: notificationColorPalette.redBg,
  },

  // ================================
  // CASE REMOVED (3 types)
  // ================================
  [EventPayloadId.CASE_REMOVED_CASE_OWNER]: SHARED_CONFIGS.caseRemoved,
  [EventPayloadId.CASE_REMOVED_CASE_OPEN_NEGOTIATION]:
    SHARED_CONFIGS.caseRemoved,
  [EventPayloadId.CASE_EXPIRED_OWNER]: SHARED_CONFIGS.caseRemoved,

  // ================================
  // CLIENT REJECTED (3 types)
  // ================================
  [EventPayloadId.CASE_REJECTED_OWNER]: SHARED_CONFIGS.clientNotSigned,
  [EventPayloadId.CASE_REJECTED_RELIST_CASE_OWNER]:
    SHARED_CONFIGS.clientNotSigned,
  [EventPayloadId.CASE_REJECTED_PROSPECT]: SHARED_CONFIGS.clientNotSigned,

  // ================================
  // POST SIGNED (5 types)
  // ================================
  [EventPayloadId.CASE_FEE_AGREEMENT_REMINDER]: {
    label: 'Upload Fee Agreement',
    description: 'Upload fee agreement for client',
    icon: DocumentPercentIcon,
    iconColor: notificationColorPalette.cyan700,
    bgColor: notificationColorPalette.cyanBg,
  },
  [EventPayloadId.CASE_FEE_AGREEMENT_REMINDER_BUYER]: {
    label: 'Fee Agreement Reminder',
    description: 'Remind seller to upload fee agreement',
    icon: DescriptionIcon,
    iconColor: notificationColorPalette.blue500,
  },
  [EventPayloadId.CASE_FEE_AGREEMENT_UPLOADED]: {
    label: 'Fee Agreement Uploaded',
    description: 'Fee agreement uploaded',
    icon: DocumentPercentIcon,
    iconColor: notificationColorPalette.cyan700,
    bgColor: notificationColorPalette.cyanBg,
  },
  [EventPayloadId.CASE_STATUS_UPDATE_REMINDER_BUYER]: {
    label: 'Update Case Status Reminder',
    description: 'Update case status for client',
    icon: DocumentAddIcon,
    iconColor: notificationColorPalette.cyan700,
    bgColor: notificationColorPalette.cyanBg,
  },
  [EventPayloadId.CASE_STATUS_UPDATE_REQUESTED_BUYER]: {
    label: 'Seller Request Update',
    description: 'Seller requested status update',
    icon: InfoIcon,
    iconColor: notificationColorPalette.amber500,
  },
  [EventPayloadId.CASE_STATUS_UPDATE_SELLER]: {
    label: 'New Status Update',
    description: 'New update for case',
    icon: InfoIcon,
    iconColor: notificationColorPalette.green500,
  },
  [EventPayloadId.CASE_STATUS_UPDATE_BUYER]: {
    label: 'New Status Update',
    description: 'New update for case',
    icon: InfoIcon,
    iconColor: notificationColorPalette.green500,
  },
  [EventPayloadId.CASE_SIGNED_OWNER]: SHARED_CONFIGS.clientSigned,
  [EventPayloadId.CASE_SIGNED_PROSPECT]: SHARED_CONFIGS.clientSigned,

  // ================================
  // MISC / ADMIN (remaining types)
  // ================================
  [EventPayloadId.ORGANIZATION_ATTORNEY_JOIN_REQUEST]: {
    label: 'New Attorney Request',
    description: 'Attorney requesting to join your law firm',
    icon: BuildingPeopleIcon,
    iconColor: notificationColorPalette.cyan700,
    bgColor: notificationColorPalette.cyanBg,
  },
  [EventPayloadId.PROPOSAL_WITHDRAW_OWNER]: SHARED_CONFIGS.proposalWithdrawn,
  [EventPayloadId.PROPOSAL_WITHDRAW_PROSPECT]: SHARED_CONFIGS.proposalWithdrawn,
  [EventPayloadId.CASE_SHARE_PC_CONTACT_INFO_PROSPECT]:
    SHARED_CONFIGS.contactInfoShared,
  // Withhold Client Contact: the seller shared a represented client's contact
  [EventPayloadId.CASE_CONTACT_SHARED_BUYER]: SHARED_CONFIGS.contactInfoShared,
  [EventPayloadId.CASE_CONTACT_SHARED_SELLER]: SHARED_CONFIGS.contactInfoShared,
  [EventPayloadId.PUBLIC_CASE_NOTIFY_USERS]: {
    label: 'New Case Available',
    description: 'New case matching your preferences',
    icon: NotificationsIcon,
    iconColor: notificationColorPalette.blue500,
  },
  [EventPayloadId.UNREAD_CHAT_MESSAGES]: {
    label: 'Unread Messages',
    description: 'You have unread chat messages',
    icon: NotificationsIcon,
    iconColor: notificationColorPalette.blue500,
  },

  // ================================
  // LCMS HEALTH MONITORING (5 types)
  // ================================
  [EventPayloadId.LCMS_HEALTH_CHECK__FAILED]: {
    label: 'LCMS Health Check Failed',
    description: 'LCMS integration health check failed',
    icon: BuildIcon,
    iconColor: notificationColorPalette.red500,
  },
  [EventPayloadId.LCMS_HEALTH_DAILY_REPORT_SLA_VIOLATION]: {
    label: 'LCMS SLA Violation',
    description: 'LCMS daily report shows SLA violation',
    icon: NotificationsActiveIcon,
    iconColor: notificationColorPalette.red500,
  },
  [EventPayloadId.LCMS_HEALTH_DAILY_REPORT_PERFORMANCE_DEGRADED]: {
    label: 'LCMS Performance Degraded',
    description: 'LCMS performance issues detected',
    icon: BuildIcon,
    iconColor: notificationColorPalette.amber500,
  },
  [EventPayloadId.LCMS_HEALTH_DAILY_REPORT_HEALTHY]: {
    label: 'LCMS Healthy',
    description: 'LCMS is operating normally',
    icon: CheckCircleIcon,
    iconColor: notificationColorPalette.green500,
  },
  [EventPayloadId.LCMS_HEALTH_DAILY_REPORT_RECOVERY]: {
    label: 'LCMS Recovered',
    description: 'LCMS has recovered from previous issues',
    icon: CheckCircleIcon,
    iconColor: notificationColorPalette.green500,
  },
}

export const AVATAR_NOTIFICATIONS = [
  EventPayloadId.PUBLIC_CASE_PROPOSAL_OWNER,
  EventPayloadId.PUBLIC_CASE_INVITE_PROSPECT,
  EventPayloadId.PUBLIC_CASE_PROPOSAL_REJECTED_BY_OWNER,
  EventPayloadId.PUBLIC_CASE_PROPOSAL_REJECTED_BY_PROSPECT,
  EventPayloadId.COUNTER_OFFER_RECEIVER,
  EventPayloadId.CASE_STATUS_UPDATE_REQUESTED_BUYER,
  EventPayloadId.CASE_STATUS_UPDATE_SELLER,
  EventPayloadId.PRIVATE_CASE_INVITE_PROSPECT,
  EventPayloadId.CASE_STATUS_UPDATE_REQUESTED_BUYER,
  EventPayloadId.CASE_STATUS_UPDATE_BUYER,
  EventPayloadId.CASE_STATUS_UPDATE_SELLER,
  // Broadcast notifications use seller avatar
  EventPayloadId.PRIVATE_CASE_BROADCAST_INVITE_PROSPECT,
]

/**
 * Relative time thresholds for date formatting
 */
const TIME_THRESHOLDS = {
  JUST_NOW: 60, // 1 minute
  MINUTES: 3600, // 1 hour
  HOURS: 86400, // 1 day
  DAYS: 604800, // 1 week
  WEEKS: 2592000, // 30 days
  MONTHS: 31536000, // 1 year
}

// ================================
// HELPER FUNCTIONS (declared early for hoisting)
// ================================

const formatRelativeTime = (diffInSeconds: number): string => {
  if (diffInSeconds < TIME_THRESHOLDS.JUST_NOW) {
    return 'Just now'
  }

  if (diffInSeconds < TIME_THRESHOLDS.MINUTES) {
    const minutes = Math.floor(diffInSeconds / 60)
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`
  }

  if (diffInSeconds < TIME_THRESHOLDS.HOURS) {
    const hours = Math.floor(diffInSeconds / 3600)
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
  }

  if (diffInSeconds < TIME_THRESHOLDS.DAYS) {
    const days = Math.floor(diffInSeconds / 86400)
    return `${days} ${days === 1 ? 'day' : 'days'} ago`
  }

  if (diffInSeconds < TIME_THRESHOLDS.WEEKS) {
    const weeks = Math.floor(diffInSeconds / 604800)
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`
  }

  const months = Math.floor(diffInSeconds / 2592000)
  return `${months} ${months === 1 ? 'month' : 'months'} ago`
}

const formatAbsoluteDate = (
  date: Date,
  includeTime: boolean,
  timezone: string
): string => {
  if (includeTime) {
    return date.toLocaleString(undefined, {
      timeZone: timezone,
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  }

  return date.toLocaleDateString()
}

// ================================
// DATE FORMATTING UTILITIES
// ================================

/**
 * Formats a date string for display in notifications
 *
 * @param dateString - ISO date string to format
 * @param options - Formatting options
 * @returns Formatted date string
 *
 * @example
 * ```typescript
 * formatNotificationDate('2024-01-15T10:30:00Z') // "2 hours ago"
 * formatNotificationDate('2024-01-15T10:30:00Z', { relative: false }) // "Jan 15, 2024"
 * ```
 */
export function formatNotificationDate(
  dateString: string,
  options: DateFormatOptions = {}
): string {
  const {
    relative = true,
    includeTime = false,
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  } = options

  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  // Return relative time if requested and within reasonable bounds
  if (
    relative &&
    diffInSeconds >= 0 &&
    diffInSeconds < TIME_THRESHOLDS.MONTHS
  ) {
    return formatRelativeTime(diffInSeconds)
  }

  // Return absolute date
  return formatAbsoluteDate(date, includeTime, timezone)
}

/**
 * Formats a date string as absolute date and time
 *
 * @param dateString - ISO date string to format
 * @returns Formatted date string (e.g., "May 6, 2025 - 10:30 AM")
 *
 * @example
 * ```typescript
 * formatAbsoluteDateTime('2025-05-06T10:30:00Z') // "May 6, 2025 - 10:30 AM"
 * ```
 */
export function formatAbsoluteDateTime(dateString: string): string {
  const date = new Date(dateString)

  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }

  const datePart = date.toLocaleDateString(undefined, dateOptions)
  const timePart = date.toLocaleTimeString(undefined, timeOptions)

  return `${datePart} - ${timePart}`
}

// ================================
// PRIORITY UTILITIES
// ================================
// Note: Priority utilities removed as they were unused in production code.
// PRIORITY_CONFIGS constant kept for potential future use.

// ================================
// NOTIFICATION TYPE UTILITIES
// ================================

/**
 * Gets the configuration for a notification type
 * Logs a warning if the type is not found in the configuration
 *
 * @param type - Notification type
 * @returns Type configuration object
 *
 * @example
 * ```typescript
 * const config = getNotificationTypeConfig('case__matched__owner')
 * console.log(config.label) // "Case Matched"
 * ```
 */
export function getNotificationTypeConfig(
  type: NotificationType
): NotificationTypeConfig {
  const config = NOTIFICATION_TYPE_CONFIGS[type]

  if (!config) {
    // eslint-disable-next-line no-console
    console.warn(
      `⚠️  No display config found for notification type: "${type}"`,
      '\nThis type should be added to NOTIFICATION_TYPE_CONFIGS in notificationHelpers.ts',
      '\nUsing fallback display config.'
    )

    return {
      label: 'Notification',
      description: `Notification type: ${type}`,
      icon: NotificationsIcon,
      iconColor: notificationColorPalette.cyan700,
      bgColor: notificationColorPalette.cyanBg,
    }
  }

  return config
}
